import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { setAuthData } from "../helpers/authHelper";
import { showSuccess } from "../components/ToastNotifications";
import AuthShowcase, { PasswordField, SubmitButton, TextField } from "../components/auth/AuthShowcase";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resending, setResending] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await apiRequest("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password, rememberMe })
      });
      setAuthData(result.token, result.user, rememberMe);
      showSuccess(`Welcome back${result.user.firstName ? `, ${result.user.firstName}` : ""}.`);
      if (result.user.role === "admin") navigate("/admin/dashboard", { replace: true });
      else {
        const returnTo = location.state?.from;
        const returnPath = returnTo && typeof returnTo.pathname === "string" &&
          returnTo.pathname.startsWith("/") &&
          !returnTo.pathname.startsWith("//") &&
          !["/login", "/signup"].includes(returnTo.pathname)
          ? `${returnTo.pathname}${returnTo.search || ""}${returnTo.hash || ""}`
          : null;
        navigate(result.user.onboardingComplete ? returnPath || "/dashboard" : "/onboarding", { replace: true });
      }
    } catch (requestError) {
      setError(requestError.message);
      setVerificationRequired(requestError.data?.code === "EMAIL_NOT_VERIFIED");
      setResendMessage("");
    } finally {
      setSubmitting(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    setError("");
    setResendMessage("");
    try {
      const result = await apiRequest("/api/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() })
      });
      setResendMessage(result.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShowcase
      eyebrow="AI-powered social media workspace"
      title={<>Grow your business <em>on social, with AI.</em></>}
      intro="Create on-brand posts, plan your content calendar, reply to messages and capture leads — all from one workspace."
    >
      <header className="auth-card-head">
        <h2>Welcome back</h2>
        <p>Sign in to manage your posts, calendar and customer messages.</p>
      </header>

      <form className="auth-form" onSubmit={submit}>
        {error && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
        <TextField id="login-email" label="Email address" icon="ti-mail" type="email" autoComplete="email" maxLength={254} placeholder="you@business.com" value={email} onChange={(event) => setEmail(event.target.value)} required />
        <PasswordField id="login-password" label="Password" autoComplete="current-password" placeholder="Enter your password" value={password} onChange={(event) => setPassword(event.target.value)} required />
        <div className="auth-options">
          <label className="auth-check" htmlFor="login-remember-me">
            <input id="login-remember-me" type="checkbox" checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} />
            Remember me
          </label>
          <Link className="auth-link" to="/forgot-password">Forgot password?</Link>
        </div>
        <SubmitButton busy={submitting} busyLabel="Signing in…">Sign in</SubmitButton>
      </form>

      {verificationRequired && <div className="auth-form auth-resend">
        <button className="auth-submit is-secondary" type="button" onClick={resendVerification} disabled={resending || !email.trim()}>
          {resending ? <><span className="auth-spinner" aria-hidden="true" /> Sending…</> : <><i className="ti ti-mail-forward" aria-hidden="true" /> Resend verification email</>}
        </button>
        {resendMessage && <div className="auth-alert is-success" role="status"><i className="ti ti-circle-check" aria-hidden="true" /><span>{resendMessage}</span></div>}
      </div>}

      <p className="auth-switch">New to Socialyorix? <Link className="auth-link" to="/signup">Create an account</Link></p>
    </AuthShowcase>
  );
};

export default Login;
